import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { DOOR_TYPES, MOCK_PRODUCTS } from '../data/mockData';
import { 
  Package, 
  Plus, 
  Trash2, 
  Wand2, 
  DoorOpen,
  Ruler,
  Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { ProductAssignment } from './ProductAssignment';
import { cn } from '../lib/utils';

export function DoorDetail({ door, floor, building }) {
  const { actions } = useApp();
  const [showProductModal, setShowProductModal] = useState(false);
  const [attributes, setAttributes] = useState(door.attributes || { width: 800, height: 2100 });

  const handleTypeChange = (value) => {
    actions.updateDoor(building.id, floor.id, door.id, { type: value });
    toast.success(`Door type updated to ${value}`);
  };

  const handleAttributeChange = (key, value) => {
    const newAttributes = { ...attributes, [key]: parseInt(value) || 0 };
    setAttributes(newAttributes);
    actions.updateDoor(building.id, floor.id, door.id, { attributes: newAttributes });
  };

  const handleRemoveProduct = (productId) => {
    actions.removeProductFromDoor(building.id, floor.id, door.id, productId);
    toast.success('Product removed');
  };

  const handleAutoAssign = () => {
    actions.autoAssignHardware('door', building.id, floor.id);
    toast.success('Hardware auto-assigned based on door type');
  };

  // Get product details for assigned products
  const assignedProducts = door.products.map(productId => 
    MOCK_PRODUCTS.find(p => p.id === productId)
  ).filter(Boolean);

  const totalCost = assignedProducts.reduce((sum, p) => sum + p.price, 0);

  const doorTypeColors = {
    'standard': 'bg-slate-100 text-slate-700',
    'exit': 'bg-green-100 text-green-700',
    'fire-rated': 'bg-red-100 text-red-700'
  };

  return (
    <div className="space-y-6" data-testid="door-detail">
      {/* Door Info Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
            <DoorOpen className="w-6 h-6 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{door.name}</h3>
            <p className="text-sm text-muted-foreground">
              {floor.name} • {building.name}
            </p>
          </div>
        </div>
        <Badge className={doorTypeColors[door.type]}>
          {door.type === 'fire-rated' ? 'Fire-Rated' : door.type.charAt(0).toUpperCase() + door.type.slice(1)}
        </Badge>
      </div>

      {/* Door Type Selection */}
      <div className="space-y-3">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Door Type
        </Label>
        <Select value={door.type} onValueChange={handleTypeChange}>
          <SelectTrigger data-testid="door-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOOR_TYPES.map(type => (
              <SelectItem key={type.id} value={type.id}>
                <div className="flex flex-col">
                  <span>{type.name}</span>
                  <span className="text-xs text-muted-foreground">{type.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Door Attributes */}
      <div className="space-y-3">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Ruler className="w-4 h-4" />
          Dimensions (mm)
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="width" className="text-xs text-muted-foreground">Width</Label>
            <Input
              id="width"
              type="number"
              value={attributes.width}
              onChange={(e) => handleAttributeChange('width', e.target.value)}
              data-testid="door-width-input"
            />
          </div>
          <div>
            <Label htmlFor="height" className="text-xs text-muted-foreground">Height</Label>
            <Input
              id="height"
              type="number"
              value={attributes.height}
              onChange={(e) => handleAttributeChange('height', e.target.value)}
              data-testid="door-height-input"
            />
          </div>
        </div>
      </div>

      {/* Assigned Products Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Package className="w-4 h-4" />
            Assigned Products
            {assignedProducts.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({assignedProducts.length} items)
              </span>
            )}
          </Label>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleAutoAssign}
              data-testid="auto-assign-btn"
            >
              <Wand2 className="w-4 h-4 mr-1" />
              Auto Assign
            </Button>
            <Button 
              size="sm"
              onClick={() => setShowProductModal(true)}
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Products List */}
        {assignedProducts.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-6 text-center">
            <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No products assigned</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click "Add Product" or use "Auto Assign" to add hardware
            </p>
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {assignedProducts.map((product) => (
              <div 
                key={product.id} 
                className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                data-testid={`assigned-product-${product.id}`}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium">{product.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{product.type}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">
                    ${product.price.toFixed(2)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemoveProduct(product.id)}
                    data-testid={`remove-product-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Total */}
            <div className="flex items-center justify-between p-3 bg-muted/50">
              <span className="text-sm font-medium">Total</span>
              <span className="text-sm font-semibold">${totalCost.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Product Assignment Modal */}
      <ProductAssignment
        open={showProductModal}
        onClose={() => setShowProductModal(false)}
        door={door}
        building={building}
        floor={floor}
      />
    </div>
  );
}
